import { ShieldCheck, GraduationCap, Wallet, Stethoscope } from 'lucide-react';
import StudentVisaGuideShell from './components/StudentVisaGuideShell';

/**
 * Canada Student Visa (Study Permit) — config object consumed by the shared
 * StudentVisaGuideShell. All rendering logic lives in the shell.
 */

const CONFIG = {
  testidPrefix: 'ca-visa',

  seo: {
    title: 'Canada Student Visa (Study Permit) Guide 2026: SDS + PAL Roadmap',
    description: 'Canada Study Permit 2026 for Indians: CAD 22,895 GIC, PAL requirement, SDS stream, IELTS/PTE, document checklist & 8-step process.',
  },

  breadcrumb: [
    { name: 'Home', url: 'https://zafco.in/' },
    { name: 'Study Abroad', url: 'https://zafco.in/study-abroad/' },
    { name: 'Canada', url: 'https://zafco.in/study-abroad/canada/' },
    { name: 'Student Visa Guide' },
  ],

  hero: {
    eyebrow: 'Canada Visa Guide · 2026',
    lastUpdated: '28 Feb 2026',
    titlePre: 'Canada Study Permit',
    titleAccent: '2026',
    summaryHtml: 'The complete no-fail roadmap for Indian students with a Letter of Acceptance from a DLI-listed institution. Updated for 2026 — including the <strong>CAD 22,895 GIC</strong>, the <strong>Provincial Attestation Letter (PAL)</strong>, and the <strong>24-hour/week</strong> off-campus work cap.',
    primaryCta: 'Book Free Visa Counselling',
    primaryHref: '/study-abroad/book-counselling/?source=study-abroad-canada-student-visa',
    secondaryCta: 'Evaluate My Profile',
    secondaryHref: '/study-abroad/free-profile-evaluation/?source=study-abroad-canada-student-visa-hero&country=canada',
  },

  snapshot: {
    eyebrow: '2026 GIC Requirement',
    amount: 'CAD 22,895',
    subLabel: 'SDS & non-SDS · 12-month proof',
    paragraphsHtml: [
      '<strong class="text-amber-900">The single most decisive number in your Study Permit.</strong> The previous CAD 20,635 amount is obsolete — IRCC raised the GIC to <strong>CAD 22,895</strong> effective 1 Jan 2024, with annual indexation from 2025.',
      'CAD 2,000 is released on arrival; the balance is disbursed in monthly tranches. Scotiabank, ICICI, CIBC and SBI are the four GIC-accepted banks.',
    ],
  },

  timeline: {
    heading: { eyebrow: 'When to Start', title: 'Eligibility & Timeline Matrix', subtitle: 'Backwards-plan from your intake date — the PAL alone can consume 4–6 weeks before IRCC submission.' },
    rows: [
      { window: '5–6 months before intake', action: 'Receive Letter of Acceptance (LoA) from a DLI-listed institution.' },
      { window: '4–5 months before',         action: 'Apply for Provincial Attestation Letter (PAL) through the province.' },
      { window: '3–4 months before',         action: 'Purchase GIC (CAD 22,895) + pay first-semester tuition.' },
      { window: '2–3 months before',         action: 'Submit Study Permit application via IRCC portal (SDS or non-SDS).' },
      { window: '1–2 months before',         action: 'Biometrics at VFS Global + (if required) medical examination.' },
      { window: '4–8 weeks after',           action: 'Decision window — 70% of SDS applicants from India clear in 4–6 weeks.' },
    ],
  },

  financialProof: {
    heading: { eyebrow: 'Money in the Bank', title: 'Financial Evidence Breakdown', subtitle: 'IRCC wants total liquid funds equivalent to CAD 22,895 + first-year tuition + 10–15% buffer. Spread evidence across GIC, parental FDs, and education loan.' },
    rows: [
      { item: 'GIC (Guaranteed Investment Certificate)', amount: 'CAD 22,895',           note: 'Raised from CAD 20,635 effective 1 Jan 2024. CAD 2,000 released on arrival; balance disbursed monthly.' },
      { item: 'First-year tuition (paid)',                amount: 'CAD 13,000 – 35,000',  note: 'Receipt mandatory for SDS stream.' },
      { item: 'Education-loan / parental funds',          amount: 'CAD 10,000 – 15,000 buffer', note: 'For travel, books, emergencies.' },
      { item: 'Spouse (if accompanying)',                  amount: '+ CAD 4,000 / yr',    note: 'Add to primary applicant funds.' },
      { item: 'Each child (if accompanying)',              amount: '+ CAD 3,000 / yr',    note: 'Plus tuition for school-age children.' },
      { item: 'Biometric + visa application fee',          amount: 'CAD 235',             note: 'Visa CAD 150 + biometric CAD 85.' },
    ],
    cta: {
      tag: 'Financial Setup',
      heading: 'Confused About GIC + Loan Stacking?',
      copy: 'Our Canada team structures the GIC + education-loan + sponsor combo for SDS-grade approval — typically in 48 hours.',
      button: 'Get Financial Setup Support',
      href: '/study-abroad/book-counselling/?source=study-abroad-canada-student-visa-cta-financial',
      testid: 'ca-visa-cta-financial',
    },
  },

  policyAlerts: {
    heading: { eyebrow: 'Critical 2024–26 Policy Changes', title: 'What Has Changed Since 2023', subtitle: 'Three policy shifts dramatically affect your application strategy. All three are non-negotiable for 2026 intakes.' },
    items: [
      { date: '22 Jan 2024', title: 'Provincial Attestation Letter (PAL) Mandatory',
        body: 'IRCC introduced the PAL requirement to cap the number of study-permit applications per province. Every applicant must now secure a PAL from the destination province BEFORE submitting the IRCC Study Permit form. No PAL = automatic refusal.' },
      { date: '1 Jan 2024',  title: 'GIC Raised to CAD 22,895',
        body: 'The minimum GIC (Guaranteed Investment Certificate) for cost-of-living proof was raised from CAD 20,635 to CAD 22,895 — the first major increase since 2003. The figure will be indexed annually from 2025.' },
      { date: '8 Nov 2024',  title: 'Off-Campus Work Capped at 24 Hours / Week',
        body: 'IRCC permanently capped off-campus work at 24 hours/week during semester (up from the previous 20-hour cap, but below the temporary unlimited-hours allowance ended Apr 2024). Full-time work allowed during scheduled breaks.' },
    ],
  },

  checklists: {
    heading: { eyebrow: 'Zero-Error Edition', title: 'The Complete Document Checklist', subtitle: 'Four categories. Upload PDFs ≤ 4 MB each on the IRCC portal. Mislabelled or missing files are the #1 cause of procedural fairness emails.' },
    cards: [
      { tag: 'Identity', Icon: ShieldCheck, tone: 'blue', items: [
        'Valid passport — ≥6 months validity beyond intended stay',
        'Digital passport-size photo (35×45mm) meeting IRCC specs',
        'Birth certificate / 10th-grade marksheet for DOB proof',
        'Two reference letters from prior institution / employer',
      ]},
      { tag: 'Academic', Icon: GraduationCap, tone: 'gold', items: [
        'Letter of Acceptance (LoA) from a Designated Learning Institution (DLI)',
        'Provincial Attestation Letter (PAL) — mandatory since 22 Jan 2024',
        'Academic transcripts (10th, 12th, Bachelor\u2019s — apostilled)',
        'English proficiency — IELTS 6.0 (Acad) / PTE 60 / TOEFL 83 / Duolingo 115',
        'Statement of Purpose (SOP) — 800–1,200 words covering study plan',
      ]},
      { tag: 'Financial', Icon: Wallet, tone: 'emerald', items: [
        'GIC certificate of CAD 22,895 from Scotiabank / ICICI / CIBC / SBI',
        'First-year tuition fee receipt (mandatory for SDS)',
        'Education loan sanction letter (if applicable)',
        '6-month sponsor bank statements + 3 years ITR',
        'Sponsor employment / business proof + income declaration',
      ]},
      { tag: 'Health', Icon: Stethoscope, tone: 'amber', items: [
        'Upfront medical examination (UMI) via IRCC-panel hospital',
        'Police Clearance Certificate (if asked for)',
        'Statement of intent confirming return after studies',
        'Provincial health insurance plan registration intent (varies by province)',
      ]},
    ],
  },

  providers: {
    heading: { eyebrow: 'GIC Deep-Dive', title: 'GIC Bank Provider Comparison 2026', subtitle: 'All four are SDS-accepted. Choose by setup fee, India-side support, or certificate turnaround.' },
    columns: [
      { key: 'name',      label: 'Provider',   bold: true },
      { key: 'gicAmount', label: 'GIC Amount', bold: true, accent: true },
      { key: 'setup',     label: 'Setup Fee' },
      { key: 'verdict',   label: 'Verdict',    hide: 'hidden lg:table-cell' },
    ],
    rows: [
      { name: 'Scotiabank',        gicAmount: 'CAD 22,895', setup: 'CAD 200', verdict: 'Largest SDS share · 40% of Indian students' },
      { name: 'ICICI Bank Canada', gicAmount: 'CAD 22,895', setup: 'CAD 0',   verdict: 'Easiest for Indian applicants · Hindi support' },
      { name: 'CIBC',              gicAmount: 'CAD 22,895', setup: 'CAD 200', verdict: 'Fastest GIC certificate (2–3 business days)' },
      { name: 'SBI Canada Bank',   gicAmount: 'CAD 22,895', setup: 'CAD 0',   verdict: 'Indian SBI account holders preferred' },
    ],
  },

  steps: {
    heading: { eyebrow: 'Process Flow', title: 'The 8-Step Study Permit Application', subtitle: 'Linear, accountable, tracked. Each step has a single deliverable — no parallel scrambling.' },
    items: [
      { title: 'Accept LoA',          desc: 'Pay tuition deposit, download the official Letter of Acceptance from your DLI institution.' },
      { title: 'Obtain PAL',          desc: 'Submit your provincial application — Ontario, BC, Alberta etc. — to receive a Provincial Attestation Letter (PAL).' },
      { title: 'Purchase GIC',        desc: 'Buy CAD 22,895 GIC from Scotiabank / ICICI / CIBC / SBI. Keep the original GIC certificate.' },
      { title: 'Pay Tuition + Visa',  desc: 'Pay first-year tuition (SDS mandatory) and the CAD 235 visa + biometric fee.' },
      { title: 'Create IRCC Account', desc: 'Register the personal IRCC online portal and start the Study Permit form.' },
      { title: 'Upload Documents',    desc: 'Upload the LoA, PAL, GIC, tuition receipt, financial evidence, SOP and language scores.' },
      { title: 'Biometrics + UMI',    desc: 'Attend VFS for biometrics and complete the Upfront Medical Examination at a panel hospital.' },
      { title: 'Await Decision',      desc: 'SDS median: 4–6 weeks. Non-SDS: 8–12 weeks. Monitor your IRCC portal weekly.' },
    ],
  },

  interview: {
    heading: { eyebrow: 'Genuine-Intent Interview', title: '5 Questions You Will Be Asked', subtitle: 'IRCC officers look for clarity, not memorised answers. Specifics beat scripts.' },
    questions: [
      'Why did you choose Canada over USA / UK / Australia?',
      'Why this specific DLI and programme?',
      'Who is sponsoring you and what is the source of funds for CAD 22,895?',
      'What is your career plan after graduation — in Canada or India?',
      'How does this course fit into your existing academic / work background?',
    ],
  },

  postArrival: {
    heading: { eyebrow: 'After Landing', title: 'First-30-Days Checklist', subtitle: 'The 5 items every new arrival must complete to stay compliant and well-positioned for the PGWP.' },
    items: [
      'Activate your Social Insurance Number (SIN) within the first 14 days.',
      'Open a Canadian bank account (Scotiabank / RBC / TD student account).',
      'Register with the international students office at your DLI.',
      'Verify the work permit — 24 hrs / week during semester (effective 8 Nov 2024).',
      'Plan the Post-Graduation Work Permit (PGWP) — apply within 180 days of completion.',
    ],
  },

  faqsHeading: { eyebrow: 'Frequently Asked', title: 'Canada Study Permit FAQs', subtitle: 'Schema-marked answers to the 8 most-asked questions on GIC, PAL, SDS, work rights and the PGWP.' },
  faqs: [
    { q: 'How much GIC do I need for a Canada Study Permit in 2026?', a: 'CAD 22,895 — raised from CAD 20,635 effective 1 January 2024. The amount is the same across SDS and non-SDS streams. CAD 2,000 is released on arrival; the balance is disbursed in monthly tranches.' },
    { q: 'What is the new Provincial Attestation Letter (PAL)?', a: 'Effective 22 January 2024, every international student must obtain a PAL from their destination province (Ontario, BC, Alberta, etc.) before submitting the IRCC Study Permit form. The provincial cap on attestations is the single biggest 2024–26 policy change.' },
    { q: 'Should I apply through SDS or non-SDS?', a: 'SDS (Student Direct Stream) is faster — 4–6 weeks median — but mandates paid first-year tuition + GIC + IELTS 6.0 (Acad) / PTE 60. Non-SDS takes 8–12 weeks but accepts education-loan sanction letters in place of paid tuition.' },
    { q: 'Can I work while on a Canadian Study Permit?', a: 'Yes — up to 24 hours per week during semester (permanent cap effective 8 November 2024), and full-time during scheduled breaks. You must be enrolled in full-time studies at a DLI.' },
    { q: 'What IELTS score is needed for SDS Canada in 2026?', a: 'IELTS Academic 6.0 overall with no band below 6.0, OR PTE Academic 60, OR TOEFL iBT 83, OR Duolingo English Test 115. Quebec DLI applications additionally accept TEF/TCF for French-taught programmes.' },
    { q: 'How long does a Canada Study Permit take to process in 2026?', a: 'SDS stream from India: 4–6 weeks median. Non-SDS: 8–12 weeks. Add 1–2 weeks for biometrics scheduling and UMI medical processing.' },
    { q: 'What is the Post-Graduation Work Permit (PGWP)?', a: 'PGWP is an open work permit issued after you complete an eligible PGWP-listed programme at a DLI. Duration: 8 months minimum (for programmes 8–24 months) up to 3 years (for 2+ year programmes). 2024 reforms restricted PGWP to specific occupation-aligned fields of study.' },
    { q: 'Can my spouse work while I study in Canada?', a: 'Effective 19 March 2024, only spouses of Master\u2019s, doctoral, and select professional-programme students qualify for an open spousal work permit. Spouses of undergraduate / college diploma students no longer qualify.' },
  ],

  anchorLinks: {
    heading: { eyebrow: 'Explore Further', title: 'Related Canada Resources', subtitle: 'Cost, PR pathways, scholarships, and the CRS calculator — everything in one cluster.' },
    items: [
      { label: 'Study in Canada',                          path: '/study-abroad/canada/' },
      { label: 'Canada CRS Calculator',                    path: '/canada-crs-calculator' },
      { label: 'Cost of Studying in Canada',               path: '/study-abroad/canada/cost-of-study/' },
      { label: 'PR Pathways from Canada',                  path: '/study-abroad/canada/pr-pathways/' },
      { label: 'Scholarships in Canada',                   path: '/study-abroad/canada/scholarships/' },
      { label: 'Free Profile Evaluation',                  path: '/study-abroad/free-profile-evaluation/?source=study-abroad-canada-student-visa-anchor' },
      { label: 'Book a Free Counselling Session',          path: '/study-abroad/book-counselling/?source=study-abroad-canada-student-visa-anchor' },
    ],
  },

  finalCta: {
    title: 'Lodge your Canada Study Permit with zero surprises.',
    subtitle: 'Our ICCRC-licensed counsellors run a 3-pass audit on every Canada file — PAL, GIC, SDS eligibility, financial structuring, SOP — before submission.',
    whatsappText: 'Hello ZAFCO - I want help with Canada Study Permit.',
    counsellingSource: 'study-abroad-canada-student-visa-final-cta',
  },
};

const CanadaStudentVisaPage = () => <StudentVisaGuideShell config={CONFIG} />;
export default CanadaStudentVisaPage;
